import {Injectable} from '@angular/core';
import {User} from "@ui-kit/framework/security/model/user.model";
import {SessionKey} from "@ui-kit/framework/security/model/session-key.enum";

/**
 * Service to manage the current user context.
 * T: the type of the user grants object
 */
@Injectable({
  providedIn: 'root'
})
export abstract class CurrentContextService<T> {
  public static readonly CLIENT_ID = 'admin-portal';

  private _currentUserInternal: User | null = null;
  private _userGrantsInternal: T[] = [];

  constructor() {
  }

  private validateSession(): boolean {
    const token = sessionStorage.getItem(SessionKey.TOKEN_KEY);
    const expiration: string | null = sessionStorage.getItem(SessionKey.TOKEN_EXPIRY_KEY) !== null ? sessionStorage.getItem(SessionKey.TOKEN_EXPIRY_KEY) : '';

    if (!token && !expiration) {
      return true;
    }

    const expirationDate = new Date(expiration!);
    const valid = token !== null && (expirationDate && expirationDate > new Date());
    if (!valid) {
      this.reset(true);
    }
    return valid;
  }

  get currentUser(): User | null {
    this._currentUserInternal = JSON.parse(sessionStorage.getItem(SessionKey.CURRENT_USER_KEY)!);
    this.validateSession();
    return this._currentUserInternal;
  }

  public set currentUser(user: User) {
    sessionStorage.setItem(SessionKey.CURRENT_USER_KEY, JSON.stringify(user));
    this._currentUserInternal = user;
  }


  set currentUserId(userId: number) {
    sessionStorage.setItem(SessionKey.CURRENT_USER_ID, userId.toString());
  }

  get currentUserId(): number | null {
    return parseInt(sessionStorage.getItem(SessionKey.CURRENT_USER_ID)!);
  }

  set userGrants(grants: T[]) {
    sessionStorage.setItem(SessionKey.USER_GRANTS_KEY, JSON.stringify(grants));
    this._userGrantsInternal = grants;
  }

  /**
   * Gets the user grants from the session or currently cached value. Note, the loading of the features
   * has been delegated to {@link AuthorizationService}
   */
  // get userGrants(): UserGrant[] | null {
  public get userGrants(): T[] {
    if (!this._userGrantsInternal || this._userGrantsInternal.length === 0) {
      this._userGrantsInternal = JSON.parse(sessionStorage.getItem(SessionKey.USER_GRANTS_KEY)!) ?? [];
    }

    this.validateSession();
    return this._userGrantsInternal;
  }

  /**
   * Retrieves the access map for the current user.
   * This method can be overridden in subclasses to provide custom filtering or mapping.
   *
   * @param {function} [filterPredicate] - Optional function to filter the access grants.
   * @returns {FlattenedUserGrant[]} An array of flattened user grants.
   */
  public accessMap(filterPredicate?: (access: T) => boolean): T[] {
    return this.filteredAccessMap(filterPredicate);
  }

  /**
   * Internal method to filter and flatten the user's access grants.
   * This method implements the core logic for retrieving and filtering access grants.
   *
   * @protected
   * @param {function} [filterPredicate] - Optional function to filter the access grants.
   * @returns {FlattenedUserGrant[]} An array of flattened and filtered user grants.
   */
  protected filteredAccessMap(filterPredicate?: (access: T) => boolean): T[] {
    const allGrants = this.userGrants ?? [];
    return filterPredicate ? allGrants.filter(filterPredicate) : allGrants;
  }

  //todo: remove all new keys made
  public reset(resetSession?: boolean): void {
    this._currentUserInternal = null;
    this._userGrantsInternal = [];

    if (resetSession) {
      sessionStorage.clear();
    }
  }
}
